import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { onContactLinkClick } from '@/lib/contactLinks';

export const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page pt-20 bg-black">
      {/* Hero Section */}
      <section className="hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-20" />
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-600/20 to-red-700/20 rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-red-500" />
            </div>
            <div>
              <h1 className="text-6xl font-bold text-white">Privacy Policy</h1>
              <p className="text-gray-400 mt-2">Effective Date: 10 April 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="content py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert prose-lg max-w-none">
            
            {/* Section 1 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">1. Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Welcome to Radius Consulting (Pty) Ltd ("Radius Consulting", "we", "us", or "our"). We are a brand marketing and growth strategy firm based in Johannesburg, South Africa. This Privacy Policy explains how we collect, use, disclose, and protect your personal information when you visit our website at radiusconsulting.co.za, submit enquiries, sign up for our newsletter, or download resources.
              </p>
              <p className="text-gray-300 leading-relaxed">
                By using our website or engaging with our services, you agree to the terms of this Privacy Policy. If you do not agree, please discontinue use of our website.
              </p>
            </div>

            {/* Section 2 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">2. Information We Collect</h2>
              
              <h3 className="text-2xl font-semibold text-white mb-4">2.1 Information you provide directly</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to us, including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-6">
                <li>Full name</li>
                <li>Email address</li>
                <li>Company name</li>
                <li>Contact number</li>
                <li>Service enquiry details and messages</li>
                <li>Information submitted via our contact form, newsletter sign-up, or resource download requests</li>
              </ul>

              <h3 className="text-2xl font-semibold text-white mb-4">2.2 Information collected automatically</h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain technical information including:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>IP address and browser type</li>
                <li>Pages visited and time spent on our site</li>
                <li>Referring website or source</li>
                <li>Device type and operating system</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">3. How We Use Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We use the personal information we collect for the following purposes:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2">
                <li>To respond to your enquiries and provide our services</li>
                <li>To send you our newsletter and marketing communications (where you have opted in)</li>
                <li>To deliver requested resources and downloadable content</li>
                <li>To improve our website, services, and user experience</li>
                <li>To comply with applicable legal obligations</li>
                <li>To protect the security and integrity of our business</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">4. Legal Basis for Processing</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We process your personal information on the following legal grounds under the Protection of Personal Information Act 4 of 2013 (POPIA) and applicable data protection law:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-3">
                <li><strong className="text-white">Consent:</strong> Where you have given us explicit consent, such as subscribing to our newsletter or submitting a contact form</li>
                <li><strong className="text-white">Legitimate interests:</strong> Where processing is necessary for our legitimate business interests, such as improving our services and communicating with prospective clients</li>
                <li><strong className="text-white">Legal obligation:</strong> Where we are required to process your information to comply with applicable laws</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">5. Sharing of Your Information</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                We do not sell, rent, or trade your personal information to third parties. We may share your information only in the following limited circumstances:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-3">
                <li><strong className="text-white">Service providers:</strong> We may share information with trusted third-party service providers who assist us in operating our website and delivering our services (such as email delivery platforms and database providers). These parties are contractually obligated to handle your data securely and in accordance with this policy.</li>
                <li><strong className="text-white">Legal requirements:</strong> We may disclose your information if required to do so by law, court order, or government authority.</li>
                <li><strong className="text-white">Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">6. Data Retention</h2>
              <p className="text-gray-300 leading-relaxed">
                We retain your personal information only for as long as is necessary to fulfil the purposes outlined in this Privacy Policy, or as required by law. Contact form submissions and newsletter subscriber data are retained for as long as you remain an active contact or subscriber. You may request deletion of your data at any time by contacting us.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">7. Your Rights</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Under POPIA and applicable data protection legislation, you have the following rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                <li>The right to access the personal information we hold about you</li>
                <li>The right to request correction of inaccurate or incomplete information</li>
                <li>The right to request deletion of your personal information</li>
                <li>The right to object to or restrict the processing of your information</li>
                <li>The right to withdraw consent at any time, where processing is based on consent</li>
                <li>The right to lodge a complaint with the Information Regulator of South Africa</li>
              </ul>
              <p className="text-gray-300 leading-relaxed">
                To exercise any of these rights, please contact us at <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} className="text-red-500 hover:text-red-400">mishaan@radiusconsulting.co.za</a>.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">8. Cookies</h2>
              <p className="text-gray-300 leading-relaxed">
                Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyse site traffic. You may configure your browser to refuse cookies, although this may affect the functionality of certain parts of our website.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">9. Data Security</h2>
              <p className="text-gray-300 leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, loss, misuse, or alteration. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">10. Third-Party Links</h2>
              <p className="text-gray-300 leading-relaxed">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those websites and encourage you to review their respective privacy policies.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-6">11. Changes to This Policy</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically.
              </p>
            </div>

            {/* Section 12 - Contact */}
            <div className="mb-12 bg-gradient-to-br from-gray-800 to-black rounded-2xl p-8 border border-gray-700">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <Mail className="w-8 h-8 text-red-500" />
                12. Contact Us
              </h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or the handling of your personal information, please contact us:
              </p>
              <div className="text-gray-300 space-y-2">
                <p><strong className="text-white">Radius Consulting (Pty) Ltd</strong></p>
                <p>Johannesburg, South Africa</p>
                <p>Email: <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} className="text-red-500 hover:text-red-400">mishaan@radiusconsulting.co.za</a></p>
                <p>Website: <a href="https://radiusconsulting.co.za" className="text-red-500 hover:text-red-400">radiusconsulting.co.za</a></p>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-gray-500 text-sm border-t border-gray-800 pt-8">
              © 2026 Radius Consulting (Pty) Ltd. All rights reserved.
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-16 bg-black border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-3xl font-bold text-white mb-6">Questions About Your Privacy?</h3>
          <p className="text-xl text-gray-400 mb-8">
            We're here to help. Get in touch with any concerns.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
